import { Capacitor } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'
import { Badge } from '@capawesome/capacitor-badge'

/**
 * Service for managing application badge counts on the app icon
 * Works on iOS and Android devices
 */
export const BadgeService = {
  /**
   * Set the badge count on the app icon
   * @param count The number to display on the badge (0 clears the badge)
   */
  async setBadgeCount(count: number): Promise<void> {
    try {
      if (!Capacitor.isNativePlatform()) {
        return
      }
      // Only set badge count if push notifications are granted
      const permissionsValue = await PushNotifications.checkPermissions()

      if (permissionsValue.receive !== 'granted') {
        console.log('Push notifications not granted, skipping badge count')
        return
      }

      await Badge.set({ count })
    } catch (error) {
      console.error('Failed to set badge count:', error)
    }
  },

  /**
   * Get the current badge count from the app icon
   * @returns The current badge count
   */
  async getBadgeCount(): Promise<number> {
    try {
      if (!Capacitor.isNativePlatform()) {
        return 0
      }
      const { count } = await Badge.get()
      return count
    } catch (error) {
      console.error('Failed to get badge count:', error)
      return 0
    }
  },

  /**
   * Increment the badge count by a specified amount
   * @param by Amount to increment by (defaults to 1)
   */
  async incrementBadgeCount(by: number = 1): Promise<void> {
    try {
      const currentCount = await this.getBadgeCount()
      await this.setBadgeCount(currentCount + by)
    } catch (error) {
      console.error('Failed to increment badge count:', error)
    }
  },

  /**
   * Decrement the badge count by a specified amount
   * @param by Amount to decrement by (defaults to 1)
   */
  async decrementBadgeCount(by: number = 1): Promise<void> {
    try {
      const currentCount = await this.getBadgeCount()
      const newCount = Math.max(0, currentCount - by)
      await this.setBadgeCount(newCount)
    } catch (error) {
      console.error('Failed to decrement badge count:', error)
    }
  },

  /**
   * Clear the badge (sets count to 0)
   */
  async clearBadge(): Promise<void> {
    try {
      if (!Capacitor.isNativePlatform()) {
        return
      }
      await Badge.clear()
    } catch (error) {
      console.error('Failed to clear badge:', error)
    }
  },

  /**
   * Check if the device supports badges
   * @returns True if badges are supported on this device
   */
  async isSupported(): Promise<boolean> {
    try {
      const { isSupported } = await Badge.isSupported()
      return isSupported
    } catch (error) {
      console.error('Failed to check badge support:', error)
      return false
    }
  },
}
